import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { FormData as JobFormData, Attorney, Deponent, FormFile } from '../types';
import { saveDraft as apiSaveDraft, loadDraft as apiLoadDraft, deleteDraft as apiDeleteDraft } from '../utils/api';
import type { FileReferences } from '../utils/api';
import { upgradeFormDataToV2 } from '../utils/invoiceFiles';
import { devLog } from '../utils/devTools';

declare global {
  interface Window {
    __NEW_ATTORNEY_FILES__?: Map<string, FormFile[]>;
  }
}

// File fields that live on a Deponent.
type DeponentFileField = 'transcriptFiles' | 'exhibitFiles' | 'audioFiles';

// Serialized file descriptor used when saving/submitting (no File blob, just metadata).
interface FileRef {
  filename?: string;
  originalName?: string;
  storagePath?: string;
  size?: number;
  mimetype?: string;
}

interface DeponentFileRefs {
  transcriptFiles?: FileRef[];
  exhibitFiles?: FileRef[];
  audioFiles?: FileRef[];
}

interface OrderRef {
  deponentId: string;
  orderSubmissionFiles: FileRef[];
}

interface AttorneyFileRefs {
  orders?: Record<number, OrderRef>;
}

interface ExtractedFileRefs {
  deponents: Record<string, DeponentFileRefs>;
  attorneys: Record<string, AttorneyFileRefs>;
  invoiceFiles?: FileRef[];
}

interface FormContextType {
  formData: JobFormData;
  validationErrors: {
    [key: string]: string;
  };
  isFormDirty: boolean;
  submitSuccess: boolean;
  draftStatus: {
    isSaving: boolean;
    isLoading: boolean;
    lastSaved?: Date;
    error?: string;
    savedStep?: number;
  };
  updateFormData: <K extends keyof JobFormData>(fieldName: K, value: JobFormData[K]) => void;
  updateNestedFormData: <
    K extends 'deponents' | 'attorneys',
    F extends keyof JobFormData[K][number]
  >(arrayName: K, index: number, fieldName: F, value: JobFormData[K][number][F]) => void;
  updateMultipleNestedFields: <K extends 'deponents' | 'attorneys'>(
    arrayName: K,
    index: number,
    fields: Partial<JobFormData[K][number]> | ((currentItem: JobFormData[K][number]) => Partial<JobFormData[K][number]>)
  ) => void;
  addItemToArray: <K extends 'deponents' | 'attorneys'>(arrayName: K, newItem: JobFormData[K][number]) => void;
  removeItemFromArray: (arrayName: 'deponents' | 'attorneys', index: number) => void;
  handleFileUpload: (fieldName: string, files: FileList | FormFile[], deponentId?: string) => void;
  removeFile: (fieldName: string, index: number, deponentId?: string) => void;
  resetForm: () => void;
  submitForm: () => Promise<{ success: boolean; error?: string; confirmationId?: string }>;
  validateStep: (stepId: string) => boolean;
  saveDraft: (currentStep: number) => Promise<{ success: boolean; error?: string }>;
  deleteDraft: () => Promise<{ success: boolean; error?: string }>;
}

// Create context
const FormContext = createContext<FormContextType | undefined>(undefined);

// Current worksheet payload schema version. 1.0 = legacy single invoiceFile.
// 1.1 = transitional dual-write. 2.0 = invoiceFiles array only.
const CURRENT_SCHEMA_VERSION = '2.0';

// Initial form state structure
const initialFormState: JobFormData = {
  // Schema version for the worksheet payload
  schemaVersion: CURRENT_SCHEMA_VERSION,

  // Basic job info
  jobNumber: '',
  jobDate: '',
  jobStartTime: '',
  reporterName: '',
  reporterEmail: '',
  caseName: '',
  jobState: '',
  jobType: '',
  depositionType: '',

  // Deponent information
  deponents: [],

  // Attorney information
  attorneys: [],

  // Real Legal status
  isRealLegal: false,

  // Additional services per deponent
  deponentServices: {},

  // Pay information
  attendanceType: '',
  waitingTime: undefined,
  writingTime: undefined,
  attendanceFee: '',
  payrollInfo: '',
  invoiceFiles: [],
  specialNotes: ''
};

interface FormProviderProps {
  children: ReactNode;
  initialData?: Partial<JobFormData>;
}

export const FormProvider: React.FC<FormProviderProps> = ({ children, initialData }) => {
  // Store the original initial data for reset functionality
  // This will be set once when the component initializes
  const [originalInitialData] = useState<JobFormData>(() => {
    const mergedData = initialData ? { ...initialFormState, ...initialData } as JobFormData : initialFormState;
    
    // Process server data for the form
    
    // Ensure reporterName and reporterEmail are set
    if (!mergedData.reporterName || !mergedData.reporterEmail) {
      const reporterName = initialData?.reporterName;
      const reporterEmail = initialData?.reporterEmail;
      if (reporterName && !mergedData.reporterName) {
        mergedData.reporterName = reporterName;
      } else if (!mergedData.reporterName) {
        mergedData.reporterName = '';
      }
      if (reporterEmail && !mergedData.reporterEmail) {
        mergedData.reporterEmail = reporterEmail;
      } else if (!mergedData.reporterEmail) {
        mergedData.reporterEmail = '';
      }
      // If still missing, set to empty strings
      if (!mergedData.reporterName) mergedData.reporterName = '';
      if (!mergedData.reporterEmail) mergedData.reporterEmail = '';
    }
    
    // Ensure we have deponents and attorneys arrays
    if (!mergedData.deponents || !Array.isArray(mergedData.deponents)) {
      mergedData.deponents = [];
    } else {
      // Process deponents to extract UI time fields from 24-hour format strings
      mergedData.deponents = mergedData.deponents.map(deponent => {
        const processedDeponent = { ...deponent };
        
        // Convert startTime (HH:MM) to UI fields if they don't exist
        if (deponent.startTime && 
            (!deponent.startTimeHour || !deponent.startTimeMinute || !deponent.startTimeAmPm)) {
          try {
            const [hours, minutes] = deponent.startTime.split(':');
            let hour = parseInt(hours, 10);
            const minute = minutes;
            let ampm: 'AM' | 'PM' = 'AM';
            
            // Convert 24-hour to 12-hour format
            if (hour >= 12) {
              ampm = 'PM';
              if (hour > 12) hour -= 12;
            }
            if (hour === 0) hour = 12;
            
            processedDeponent.startTimeHour = hour.toString().padStart(2, '0');
            processedDeponent.startTimeMinute = minute.padStart(2, '0');
            processedDeponent.startTimeAmPm = ampm;
          } catch (error) {
            console.error('Error parsing deponent startTime:', error);
          }
        }
        
        // Convert endTime (HH:MM) to UI fields if they don't exist
        if (deponent.endTime && 
            (!deponent.endTimeHour || !deponent.endTimeMinute || !deponent.endTimeAmPm)) {
          try {
            const [hours, minutes] = deponent.endTime.split(':');
            let hour = parseInt(hours, 10);
            const minute = minutes;
            let ampm: 'AM' | 'PM' = 'AM';
            
            // Convert 24-hour to 12-hour format
            if (hour >= 12) {
              ampm = 'PM';
              if (hour > 12) hour -= 12;
            }
            if (hour === 0) hour = 12;
            
            processedDeponent.endTimeHour = hour.toString().padStart(2, '0');
            processedDeponent.endTimeMinute = minute.padStart(2, '0');
            processedDeponent.endTimeAmPm = ampm;
          } catch (error) {
            console.error('Error parsing deponent endTime:', error);
          }
        }
        
        return processedDeponent;
      });
    }
    
    if (!mergedData.attorneys || !Array.isArray(mergedData.attorneys)) {
      mergedData.attorneys = [];
    }
    
    // Ensure attorney data is properly structured
    if (mergedData.attorneys && mergedData.attorneys.length > 0) {
      mergedData.attorneys = mergedData.attorneys.map(attorney => {
        // We've removed additionalServices from the attorney level and moved to transcript orders
        
        // Ensure transcriptOrders is initialized and has an entry for each deponent
        if (!attorney.transcriptOrders) {
          attorney.transcriptOrders = [];
        }
        
        // Get all deponent IDs that should have transcript orders
        const deponentIds = mergedData.deponents.map(deponent => deponent.id);
        
        // Create a map of existing orders by deponent ID
        const existingOrdersByDeponentId = new Map();
        attorney.transcriptOrders.forEach(order => {
          if (order.deponentId) {
            existingOrdersByDeponentId.set(order.deponentId, order);
          }
        });
        
        // Create a complete set of transcript orders, preserving existing ones
        const completeTranscriptOrders = deponentIds.map(deponentId => {
          if (existingOrdersByDeponentId.has(deponentId)) {
            return existingOrdersByDeponentId.get(deponentId);
          } else {
            return {
              deponentId: deponentId,
              orderType: 'No Order',
              additionalServices: {
                realtime: false,
                roughDraft: false,
                depositionSummary: false,
                techExpert: false,
                telephonic: false,
                video: false,
                interpreter: false
              }
            };
          }
        });
        
        return {
          ...attorney,
          transcriptOrders: completeTranscriptOrders
        };
      });
    }
    
    return mergedData;
  });

  // Initialize form data with the same processed initial data
  const [formData, setFormData] = useState<JobFormData>(originalInitialData);

  // Track whether we've already attempted to load a draft for this job
  const draftLoadedRef = useRef<string | null>(null);

  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [draftStatus, setDraftStatus] = useState<{
    isSaving: boolean;
    isLoading: boolean;
    lastSaved?: Date;
    error?: string;
    savedStep?: number;
  }>({ isSaving: false, isLoading: false });
  
  // Update form data
  const updateFormData = <K extends keyof JobFormData>(fieldName: K, value: JobFormData[K]): void => {
    setFormData(prevData => ({ ...prevData, [fieldName]: value } as JobFormData));
    setIsFormDirty(true);

    // Clear validation error when field is updated
    if (validationErrors[fieldName]) {
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };
  
  // Update nested form data (for arrays like deponents and attorneys)
  const updateNestedFormData = <
    K extends 'deponents' | 'attorneys',
    F extends keyof JobFormData[K][number]
  >(arrayName: K, index: number, fieldName: F, value: JobFormData[K][number][F]): void => {
    setFormData(prevData => {
      const updatedArray = [...(prevData[arrayName] as (Deponent | Attorney)[])];
      updatedArray[index] = { ...updatedArray[index], [fieldName]: value } as (Deponent | Attorney);
      return { ...prevData, [arrayName]: updatedArray } as JobFormData;
    });
    setIsFormDirty(true);
  };
  
  // Update multiple fields on the same array item atomically (single state update).
  // Accepts either a plain object of fields or a callback that receives the current item
  // and returns the fields to update (for deriving values from the latest state).
  const updateMultipleNestedFields = <K extends 'deponents' | 'attorneys'>(
    arrayName: K,
    index: number,
    fields: Partial<JobFormData[K][number]> | ((currentItem: JobFormData[K][number]) => Partial<JobFormData[K][number]>)
  ): void => {
    setFormData(prevData => {
      const updatedArray = [...(prevData[arrayName] as (Deponent | Attorney)[])];
      const currentItem = updatedArray[index] as JobFormData[K][number];
      const resolvedFields = typeof fields === 'function' ? fields(currentItem) : fields;
      updatedArray[index] = { ...updatedArray[index], ...resolvedFields } as (Deponent | Attorney);
      return { ...prevData, [arrayName]: updatedArray } as JobFormData;
    });
    setIsFormDirty(true);
  };

  // Add item to array (deponent or attorney)
  const addItemToArray = <K extends 'deponents' | 'attorneys'>(
    arrayName: K,
    newItem: JobFormData[K][number]
  ): void => {
    const deponentItem = newItem as Deponent;
    devLog(`Adding item to ${arrayName} array:`, {
      id: newItem.id,
      name: newItem.name,
      transcriptFiles: deponentItem.transcriptFiles ? `${deponentItem.transcriptFiles.length} files` : 'none',
      exhibitFiles: deponentItem.exhibitFiles ? `${deponentItem.exhibitFiles.length} files` : 'none',
      audioFiles: deponentItem.audioFiles ? `${deponentItem.audioFiles.length} files` : 'none'
    });

    setFormData(prevData => {
      const updatedItem = { ...newItem };

      // For attorneys, check if we have any associated files in the global storage
      if (arrayName === 'attorneys' && updatedItem.id) {
        const newAttorneyFiles = window.__NEW_ATTORNEY_FILES__;

        if (newAttorneyFiles && newAttorneyFiles.size > 0) {
          devLog(`Checking for files for attorney ID: ${updatedItem.id}`);
          
          // Look for files for each deponent for this attorney
          const attorneyItem = updatedItem as Attorney;
          if (attorneyItem.transcriptOrders && Array.isArray(attorneyItem.transcriptOrders)) {
            attorneyItem.transcriptOrders = attorneyItem.transcriptOrders.map(order => {
              const deponentId = order.deponentId;
              const key = `${updatedItem.id}:${deponentId}`;

              if (newAttorneyFiles.has(key)) {
                const files = newAttorneyFiles.get(key) ?? [];
                devLog(`Found ${files.length} files for key ${key}, adding to transcriptOrder`);
                
                return { ...order, orderSubmissionFiles: files };
              }

              return order;
            });
          }
        }
      }

      // Ensure file arrays are properly copied (not references)
      if (arrayName === 'deponents') {
        const dep = updatedItem as Deponent;
        if (dep.transcriptFiles && dep.transcriptFiles.length > 0) {
          devLog(`Deponent has ${dep.transcriptFiles.length} transcript files to preserve:`,
            dep.transcriptFiles.map(f => f.name || f.originalName || 'Unknown'));
          dep.transcriptFiles = [...dep.transcriptFiles];
        }
        if (dep.exhibitFiles && dep.exhibitFiles.length > 0) {
          devLog(`Deponent has ${dep.exhibitFiles.length} exhibit files to preserve:`,
            dep.exhibitFiles.map(f => f.name || f.originalName || 'Unknown'));
          dep.exhibitFiles = [...dep.exhibitFiles];
        }
        if (dep.audioFiles && dep.audioFiles.length > 0) {
          devLog(`Deponent has ${dep.audioFiles.length} audio files to preserve:`,
            dep.audioFiles.map(f => f.name || f.originalName || 'Unknown'));
          dep.audioFiles = [...dep.audioFiles];
        }
      }

      const updatedData = {
        ...prevData,
        [arrayName]: [...(prevData[arrayName] as (Deponent | Attorney)[]), updatedItem]
      } as JobFormData;

      // Debug log to verify files are preserved
      if (arrayName === 'deponents') {
        const addedDeponent = updatedData.deponents[updatedData.deponents.length - 1];
        devLog(`Added deponent now has: 
          - transcriptFiles: ${addedDeponent.transcriptFiles ? addedDeponent.transcriptFiles.length : 0} files
          - exhibitFiles: ${addedDeponent.exhibitFiles ? addedDeponent.exhibitFiles.length : 0} files
          - audioFiles: ${addedDeponent.audioFiles ? addedDeponent.audioFiles.length : 0} files`
        );

        // Special handling when adding a new deponent - add it to all attorneys' transcript orders
        updatedData.attorneys = updatedData.attorneys.map(attorney => {
          const newTranscriptOrder = {
            deponentId: newItem.id,
            orderType: 'No Order' as const,
            additionalServices: {
              realtime: false,
              roughDraft: false,
              depositionSummary: false,
              techExpert: false,
              telephonic: false,
              video: false,
              interpreter: false
            }
          };
          return {
            ...attorney,
            transcriptOrders: [...attorney.transcriptOrders, newTranscriptOrder]
          };
        });
      }

      return updatedData;
    });
    setIsFormDirty(true);
  };
  
  // Remove item from array
  const removeItemFromArray = (arrayName: 'deponents' | 'attorneys', index: number): void => {
    setFormData(prevData => {
      const items = prevData[arrayName] as (Deponent | Attorney)[];
      const itemToRemove = items[index];
      
      // Creating a copy of the updated data
      const updatedData = {
        ...prevData,
        [arrayName]: items.filter((_, i) => i !== index)
      };
      
      // Special handling when removing a deponent - remove from attorneys' transcript orders
      if (arrayName === 'deponents' && itemToRemove) {
        const deponentId = (itemToRemove as Deponent).id;
        updatedData.attorneys = updatedData.attorneys.map(attorney => ({
          ...attorney,
          transcriptOrders: attorney.transcriptOrders.filter(order => order.deponentId !== deponentId)
        }));
      }
      
      return updatedData;
    });
    setIsFormDirty(true);
  };
  
  // Handle file uploads
  const handleFileUpload = (fieldName: string, files: FileList | FormFile[], deponentId?: string): void => {
    devLog(`handleFileUpload called with fieldName=${fieldName}, deponentId=${deponentId || 'none'}, files:`, files);
    
    // Handle order submission files differently - now requiring both deponentId and attorneyId
    if (fieldName === 'orderSubmissionFiles' && deponentId) {
      // Extract attorneyId from file metadata if present
      let attorneyId: string | null = null;
      if (Array.isArray(files) && files.length > 0 && files[0].attorneyId) {
        attorneyId = files[0].attorneyId ?? null;
      }
      
      devLog(`Processing order submission files for deponentId=${deponentId}, attorneyId=${attorneyId}`);
      
      // Check if this is a new attorney ID (for new attorneys not yet added to formData)
      const isNewAttorney = attorneyId && attorneyId.startsWith('attorney-') && 
                            !formData.attorneys.some(attorney => attorney.id === attorneyId);
      
      // If this is for a new attorney (not yet in formData), we need to:
      // 1. Attach metadata to the files
      // 2. Then add them to newAttorney's transcriptOrders
      if (isNewAttorney) {
        devLog(`Handling files for new attorney ID: ${attorneyId}`);
        
        // Create organized path: deponents/{deponentId}/attorneys/{attorneyId}/file-name
        // This format aligns with how the server organizes files by deponentId first
        const filePath = `deponents/${deponentId}/attorneys/${attorneyId}/`;
        
        // Add the attorneyId, deponentId, and filePath to each file for tracking
        const filesWithMetadata: FormFile[] = (Array.isArray(files) ? files : Array.from(files) as FormFile[])
          .map(file => ({
            ...file,
            attorneyId: attorneyId ?? undefined,
            deponentId,
            storagePath: `${filePath}${file.name}`,
            uploadTime: Date.now(),
          }));
        
        // Store the enriched metadata back onto the array (only reachable when files is FormFile[])
        if (Array.isArray(files)) {
          filesWithMetadata.forEach((f, i) => { (files as FormFile[])[i] = f; });
        }
        
        devLog(`Added metadata to files for new attorney, using path structure: ${filePath}`);
        
        // Create a NEW_ATTORNEY_FILES global map to track files for new attorneys
        // This map will be used during the addItemToArray call when the attorney is added
        if (!window.__NEW_ATTORNEY_FILES__) {
          window.__NEW_ATTORNEY_FILES__ = new Map();
        }

        // Create a unique key for this attorney+deponent combination
        const key = `${attorneyId}:${deponentId}`;
        const filesArray = Array.isArray(files) ? [...files] : Array.from(files) as FormFile[];

        // Store the files in the global map
        window.__NEW_ATTORNEY_FILES__.set(key, filesArray);
        
        devLog(`Saved ${filesArray.length} files to NEW_ATTORNEY_FILES map with key ${key}`);
        return;
      }
      
      // For attorney order submission files (existing attorneys)
      setFormData(prevData => {
        const updatedAttorneys = [...prevData.attorneys];
        
        // If we don't have a specific attorneyId in the metadata, we can't reliably update the files
        if (!attorneyId) {
          console.error('Missing attorneyId for orderSubmissionFiles - unable to attribute files to an attorney');
          return prevData;
        }
        
        // Find the attorney by ID
        const attorneyIndex = updatedAttorneys.findIndex(attorney => attorney.id === attorneyId);
        if (attorneyIndex === -1) {
          console.error(`Attorney with ID ${attorneyId} not found`);
          return prevData;
        }
        
        // Find the specific order for this deponent
        const orderIndex = updatedAttorneys[attorneyIndex].transcriptOrders.findIndex(
          order => order.deponentId === deponentId
        );
        
        if (orderIndex === -1) {
          console.error(`Order for deponent ${deponentId} not found for attorney ${attorneyId}`);
          return prevData;
        }
        
        // Found the order, update it
        const updatedOrders = [...updatedAttorneys[attorneyIndex].transcriptOrders];
        
        // Initialize orderSubmissionFiles if it doesn't exist
        if (!updatedOrders[orderIndex].orderSubmissionFiles) {
          updatedOrders[orderIndex].orderSubmissionFiles = [];
        }
        
        const currentFiles = updatedOrders[orderIndex].orderSubmissionFiles || [];

        // Convert FileList to array and remove any duplicate files (by name) before adding
        const newFiles: FormFile[] = Array.isArray(files) ? files : Array.from(files);
        
        const filteredNewFiles = newFiles.filter(newFile => {
          const isDuplicate = currentFiles.some(existingFile => {
            const newName = newFile.name || (newFile as FormFile).originalName;
            const existingName = existingFile.name || existingFile.originalName;
            return existingName === newName && existingFile.size === newFile.size;
          });
          return !isDuplicate;
        });

        updatedOrders[orderIndex] = {
          ...updatedOrders[orderIndex],
          orderSubmissionFiles: [...currentFiles, ...filteredNewFiles]
        };
        
        devLog(`Updated orderSubmissionFiles:`, updatedOrders[orderIndex].orderSubmissionFiles);
        
        updatedAttorneys[attorneyIndex] = {
          ...updatedAttorneys[attorneyIndex],
          transcriptOrders: updatedOrders
        };
        
        return {
          ...prevData,
          attorneys: updatedAttorneys
        };
      });
    } else if (deponentId) {
      // For deponent-specific file uploads
      setFormData(prevData => {
        const updatedDeponents = [...prevData.deponents];
        const deponentIndex = updatedDeponents.findIndex(d => d.id === deponentId);
        
        devLog(`Found deponent at index ${deponentIndex} with ID ${deponentId}`);
        
        if (deponentIndex !== -1) {
          const fileField = fieldName as DeponentFileField;

          // Initialize the file array if it doesn't exist
          if (!updatedDeponents[deponentIndex][fileField]) {
            updatedDeponents[deponentIndex] = { ...updatedDeponents[deponentIndex], [fileField]: [] };
          }
          
          const currentFiles = (updatedDeponents[deponentIndex][fileField] ?? []) as FormFile[];

          // Convert FileList to array and remove any duplicate files (by name) before adding
          const newFiles: FormFile[] = Array.isArray(files) ? files : (Array.from(files) as FormFile[]);

          const filteredNewFiles = newFiles.filter(newFile => {
            const isDuplicate = currentFiles.some(existingFile => {
              const newName = newFile.name || newFile.originalName;
              const existingName = existingFile.name || existingFile.originalName;
              return existingName === newName && existingFile.size === newFile.size;
            });
            return !isDuplicate;
          });
          
          updatedDeponents[deponentIndex] = {
            ...updatedDeponents[deponentIndex],
            [fileField]: [...currentFiles, ...filteredNewFiles]
          };
          devLog(`Updated deponent ${fieldName} array:`, updatedDeponents[deponentIndex][fileField]);
        }
        
        return {
          ...prevData,
          deponents: updatedDeponents
        };
      });
    } else if (fieldName === 'invoiceFile') {
      // Invoice uploads append to invoiceFiles (multi-file capable). The upload
      // category name "invoiceFile" is the storage-side bucket, kept unchanged
      // for back-compat; the form-data field is invoiceFiles.
      setFormData(prevData => {
        const currentFiles: FormFile[] = Array.isArray(prevData.invoiceFiles) ? prevData.invoiceFiles : [];
        const newFiles: FormFile[] = Array.isArray(files) ? files : (Array.from(files) as FormFile[]);

        const filteredNewFiles = newFiles.filter(newFile => {
          const newName = newFile.name || newFile.originalName;
          return !currentFiles.some(existingFile => {
            const existingName = existingFile.name || existingFile.originalName;
            return existingName === newName && existingFile.size === newFile.size;
          });
        });

        return { ...prevData, invoiceFiles: [...currentFiles, ...filteredNewFiles] };
      });
    } else {
      // For any other single-field upload (no known callers today)
      devLog(`Handling single file upload for ${fieldName}`);
      const file = Array.isArray(files) ? files[0] : files[0];
      setFormData(prevData => ({
        ...prevData,
        [fieldName]: file
      }));
    }
    setIsFormDirty(true);
  };
  
  // Remove uploaded file
  const removeFile = (fieldName: string, index: number, deponentId?: string): void => {
    if (fieldName === 'orderSubmissionFiles' && deponentId) {
      // Extract attorneyId from the deponentId (our convention is now deponentId should actually be "attorneyId:deponentId")
      const parts = deponentId.split(':');
      const attorneyId = parts.length > 1 ? parts[0] : null;
      const actualDeponentId = parts.length > 1 ? parts[1] : deponentId;
      
      devLog(`Removing ${fieldName} file at index ${index} for attorney ${attorneyId}, deponent ${actualDeponentId}`);
      
      // For attorney order submission files
      setFormData(prevData => {
        // If we don't have a specific attorneyId, we can't reliably update the files
        if (!attorneyId) {
          console.error('Missing attorneyId for removing orderSubmissionFiles - unable to find attorney');
          return prevData;
        }
        
        const updatedAttorneys = [...prevData.attorneys];
        
        // Find the attorney by ID
        const attorneyIndex = updatedAttorneys.findIndex(attorney => attorney.id === attorneyId);
        if (attorneyIndex === -1) {
          console.error(`Attorney with ID ${attorneyId} not found for file removal`);
          return prevData;
        }
        
        // Find the specific order for this deponent
        const orderIndex = updatedAttorneys[attorneyIndex].transcriptOrders.findIndex(
          order => order.deponentId === actualDeponentId
        );
        
        if (orderIndex === -1) {
          console.error(`Order for deponent ${actualDeponentId} not found for attorney ${attorneyId}`);
          return prevData;
        }
        
        // Found the order, update it
        const updatedOrders = [...updatedAttorneys[attorneyIndex].transcriptOrders];
        const currentFiles = updatedOrders[orderIndex].orderSubmissionFiles || [];

        if (currentFiles && Array.isArray(currentFiles)) {
          updatedOrders[orderIndex] = {
            ...updatedOrders[orderIndex],
            orderSubmissionFiles: currentFiles.filter((_, i) => i !== index)
          };
          
          updatedAttorneys[attorneyIndex] = {
            ...updatedAttorneys[attorneyIndex],
            transcriptOrders: updatedOrders
          };
          
          devLog(`Updated orderSubmissionFiles:`, updatedOrders[orderIndex].orderSubmissionFiles);
          
          return {
            ...prevData,
            attorneys: updatedAttorneys
          };
        }
        
        // If we didn't find the files or couldn't update, return the unchanged state
        return prevData;
      });
    } else if (deponentId) {
      // For deponent-specific file removals
      setFormData(prevData => {
        const updatedDeponents = [...prevData.deponents];
        const deponentIndex = updatedDeponents.findIndex(d => d.id === deponentId);

        if (deponentIndex !== -1) {
          const fileField = fieldName as DeponentFileField;
          const currentFiles = updatedDeponents[deponentIndex][fileField];
          if (currentFiles && Array.isArray(currentFiles)) {
            updatedDeponents[deponentIndex] = {
              ...updatedDeponents[deponentIndex],
              [fileField]: currentFiles.filter((_, i) => i !== index)
            };
          }
        }

        return { ...prevData, deponents: updatedDeponents };
      });
    } else if (fieldName === 'invoiceFile') {
      // Remove a single invoice file by index from invoiceFiles.
      setFormData(prevData => {
        const currentFiles: FormFile[] = Array.isArray(prevData.invoiceFiles) ? prevData.invoiceFiles : [];
        return { ...prevData, invoiceFiles: currentFiles.filter((_, i) => i !== index) };
      });
    } else {
      // For any other single-field file (no known callers today)
      devLog(`Removing single file for ${fieldName}`);
      setFormData(prevData => ({
        ...prevData,
        [fieldName]: null
      }));
    }
    setIsFormDirty(true);
  };
  
  // Reset form to original initial data (preserves job info from Snowflake)
  const resetForm = (): void => {
    setFormData(originalInitialData);
    setValidationErrors({});
    setIsFormDirty(false);
    setSubmitSuccess(false);
    setDraftStatus({ isSaving: false, isLoading: false, lastSaved: undefined, error: undefined });
  };
  
  // Helper function to determine if a file needs to be uploaded
  const needsUpload = (file: FormFile): boolean => {
    return !(
      file.url ||
      file.serverData ||
      file.fileReference ||
      file.storagePath ||
      file.uploaded === true
    );
  };

  // Helper function to upload a single file
  const uploadFile = async (file: File, fileType: string, deponentId?: string): Promise<{ success: boolean; file?: FormFile }> => {
    const API_URL = window.APP_CONFIG?.apiUrl || '';

    // Skip upload if the file already has a URL (already uploaded)
    if (!needsUpload(file)) {
      devLog(`File ${file.name} already has URL, skipping upload`);
      return {
        success: true,
        file: file  // Return the existing file with its URL
      };
    }
    
    try {
      // Extract the core fileType without any prefixes
      // Make sure we're only using the base fileType for server validation
      const coreFileType = fileType.includes('_') 
        ? fileType.split('_').pop() || fileType 
        : fileType;
        
      // Create form data for file upload
      const fileFormData = new FormData();
      fileFormData.append('fileType', coreFileType);
      fileFormData.append('files', file); // Note: changed from 'file' to 'files' to match server
      
      // Build the URL with query parameters
      let url = `${API_URL}/api/uploads/${formData.token}?fileType=${encodeURIComponent(coreFileType)}`;
      
      // Add deponentId as a query parameter if available
      if (deponentId) {
        url += `&deponentId=${encodeURIComponent(deponentId)}`;
      }
      
      // Upload the file using the existing endpoint
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Send the reporter session cookie, if any, for log traceability
        body: fileFormData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload file: ${errorText}`);
      }
      
      const result = await response.json();
      // Return the first file from files array (since we only uploaded one)
      return {
        success: true,
        file: result.files && result.files.length > 0 ? result.files[0] : undefined
      };
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      return { 
        success: false 
      };
    }
  };
  
  // Submit form
  const submitForm = async (): Promise<{ success: boolean; error?: string; confirmationId?: string }> => {
    const API_URL = window.APP_CONFIG?.apiUrl || '';
    
    try {
      devLog('Preparing to submit form with files...');
      
      // Create a reference object to replace file objects with file metadata
      const fileReferences: {
        deponents?: Record<string, Record<string, FormFile[]>>;
        attorneys?: Record<string, { orders: Record<number, { deponentId: string; orderSubmissionFiles: FormFile[] }> }>;
        invoiceFiles?: FormFile[];
      } = {};
      
      // Create a cleaned version of form data that only includes server-side fields
      const serverFormData = { ...formData };
      
      // Step 1: Upload deponent-specific files
      if (formData.deponents && formData.deponents.length > 0) {
        const deponentFileReferences: Record<string, Record<string, FormFile[]>> = {};
        
        for (let i = 0; i < formData.deponents.length; i++) {
          const deponent = formData.deponents[i];
          const deponentId = deponent.id;
          deponentFileReferences[deponentId] = {};
          
          for (const field of ['transcriptFiles', 'exhibitFiles', 'audioFiles']) {
            const files = deponent[field as DeponentFileField];
            if (files && Array.isArray(files) && files.length > 0) {
              devLog(`Uploading ${files.length} ${field} for deponent ${deponent.name}...`);
              
              // Upload each file in parallel, passing the deponentId separately
              const uploadPromises = files.map(file => {
                if (file instanceof File && needsUpload(file)) {
                  return uploadFile(file, field, deponentId);
                }
                return Promise.resolve({ success: true, file });
              });
              const results = await Promise.all(uploadPromises);

              const successfulUploads: FormFile[] = [];
              for (const result of results) {
                if (result.success && result.file !== undefined) {
                  successfulUploads.push(result.file);
                }
              }
              
              if (successfulUploads.length > 0) {
                deponentFileReferences[deponentId][field] = successfulUploads;
              }
            }
          }
        }
        
        // Add deponent file references if any were uploaded
        if (Object.keys(deponentFileReferences).length > 0) {
          fileReferences.deponents = deponentFileReferences;
        }
      }
      
      // Step 1b: Upload attorney order submission files
      if (formData.attorneys && formData.attorneys.length > 0) {
        const attorneyFileReferences: Record<string, { orders: Record<number, { deponentId: string; orderSubmissionFiles: FormFile[] }> }> = {};
        
        for (let i = 0; i < formData.attorneys.length; i++) {
          const attorney = formData.attorneys[i];
          const attorneyId = attorney.id;
          attorneyFileReferences[attorneyId] = { orders: {} };
          
          // Process each transcript order that has submission files
          for (let j = 0; j < attorney.transcriptOrders.length; j++) {
            const order = attorney.transcriptOrders[j];
            
            if (order.orderSubmissionFiles && Array.isArray(order.orderSubmissionFiles) && order.orderSubmissionFiles.length > 0) {
              const deponentId = order.deponentId;
              devLog(`Uploading ${order.orderSubmissionFiles.length} orderSubmissionFiles for attorney ${attorney.name}, deponent ${deponentId}...`);
              
              const uploadPromises = order.orderSubmissionFiles.map(file => {
                if (file instanceof File && needsUpload(file)) {
                  return uploadFile(file, 'orderSubmissionFiles', `${attorneyId}_${deponentId}`);
                }
                return Promise.resolve({ success: true, file });
              });
              const results = await Promise.all(uploadPromises);

              const successfulUploads: FormFile[] = [];
              for (const result of results) {
                if (result.success && result.file !== undefined) {
                  successfulUploads.push(result.file);
                }
              }
              
              if (successfulUploads.length > 0) {
                if (!attorneyFileReferences[attorneyId]) {
                  attorneyFileReferences[attorneyId] = { orders: {} };
                }
                attorneyFileReferences[attorneyId].orders[j] = {
                  deponentId,
                  orderSubmissionFiles: successfulUploads
                };
              }
            }
          }
        }
        
        // Add attorney file references if any were uploaded
        if (Object.keys(attorneyFileReferences).length > 0) {
          fileReferences.attorneys = attorneyFileReferences;
        }
      }
      
      // Step 2: Upload invoice files if present and not already uploaded.
      const invoiceFilesSource: FormFile[] = Array.isArray(formData.invoiceFiles) ? formData.invoiceFiles : [];

      if (invoiceFilesSource.length > 0) {
        const uploadedInvoiceFiles: FormFile[] = [];
        for (const invoiceFile of invoiceFilesSource) {
          if (invoiceFile instanceof File && needsUpload(invoiceFile)) {
            devLog(`Uploading invoice file...`);
            const result = await uploadFile(invoiceFile, 'invoiceFile');
            if (result.success && result.file) {
              uploadedInvoiceFiles.push(result.file);
            } else {
              console.error('Failed to upload invoiceFile, server may reject submission');
            }
          } else {
            devLog(`Invoice file already uploaded, using existing file reference`);
            uploadedInvoiceFiles.push(invoiceFile);
          }
        }

        if (uploadedInvoiceFiles.length > 0) {
          fileReferences.invoiceFiles = uploadedInvoiceFiles;
        }
      }
      
      // Step 3: Create a clean version of form data with file references
      const cleanFormData = { ...serverFormData };
      
      // Our form no longer has global file arrays, but defensively clear any
      // that might be accidentally present from legacy data.
      const cleanFormAsRecord = cleanFormData as Record<string, unknown>;
      ['transcriptFiles', 'exhibitFiles', 'audioFiles'].forEach(field => {
        if (cleanFormAsRecord[field] !== undefined) {
          cleanFormAsRecord[field] = undefined;
        }
      });
      
      // Replace deponent file arrays with file references and clean UI-only fields
      if (cleanFormData.deponents) {
        cleanFormData.deponents = cleanFormData.deponents.map(deponent => {
          const deponentCopy = { ...deponent };
          const deponentId = deponent.id;
          
          // IMPORTANT: Remove UI-only time fields, keeping only the 24-hour format time strings
          const deponentSend = deponentCopy as Record<string, unknown>;
          delete deponentSend.startTimeHour;
          delete deponentSend.startTimeMinute;
          delete deponentSend.startTimeAmPm;
          delete deponentSend.endTimeHour;
          delete deponentSend.endTimeMinute;
          delete deponentSend.endTimeAmPm;
          
          // Handle file references if any
          if (fileReferences.deponents && fileReferences.deponents[deponentId]) {
            for (const field of ['transcriptFiles', 'exhibitFiles', 'audioFiles']) {
              if (deponentCopy[field as DeponentFileField]) {
                deponentSend[field] = undefined;
              }
              if (fileReferences.deponents[deponentId][field]) {
                deponentSend[field] = fileReferences.deponents[deponentId][field];
              }
            }
          } else {
            for (const field of ['transcriptFiles', 'exhibitFiles', 'audioFiles']) {
              if (deponentCopy[field as DeponentFileField]) {
                deponentSend[field] = undefined;
              }
            }
          }

          return deponentSend as unknown as Deponent;
        });
      }
      
      // Replace attorney order submission file arrays with file references
      const attorneyFileRefs = fileReferences.attorneys;
      if (attorneyFileRefs && cleanFormData.attorneys) {
        cleanFormData.attorneys = cleanFormData.attorneys.map((attorney) => {
          const attorneyCopy = { ...attorney };
          const attorneyId = attorney.id;

          if (attorneyFileRefs[attorneyId] && attorneyFileRefs[attorneyId].orders) {
            const orderReferences = attorneyFileRefs[attorneyId].orders;
            
            // Update the transcript orders with file references
            attorneyCopy.transcriptOrders = attorneyCopy.transcriptOrders.map((order, orderIndex) => {
              const orderCopy = { ...order };
              
              // If we have references for this order
              if (orderReferences[orderIndex]) {
                const orderFiles = orderReferences[orderIndex].orderSubmissionFiles;
                orderCopy.orderSubmissionFiles = orderFiles;
                devLog(`Added ${orderFiles.length} orderSubmissionFiles to order ${orderIndex} for attorney ${attorneyId}:`, 
                  orderFiles.map(f => f.name || f.originalName || 'Unknown'));
              } else {
                orderCopy.orderSubmissionFiles = undefined;
              }
              
              return orderCopy;
            });
          } else {
            // No files were uploaded for this attorney, remove any file arrays from all orders
            attorneyCopy.transcriptOrders = attorneyCopy.transcriptOrders.map(order => {
              const orderCopy = { ...order };
              if (order.orderSubmissionFiles) {
                orderCopy.orderSubmissionFiles = undefined;
              }
              return orderCopy;
            });
          }
          
          return attorneyCopy;
        });
      }
      
      // Always stamp the outgoing payload with the current schema version so
      // readers can unambiguously select the correct parse path.
      cleanFormData.schemaVersion = CURRENT_SCHEMA_VERSION;

      // Replace invoice files with file references.
      cleanFormData.invoiceFiles = fileReferences.invoiceFiles ?? [];
      
      devLog('Submitting form with file references...');

      // Verify attorney order submission files
      if (cleanFormData.attorneys && cleanFormData.attorneys.length > 0) {
        cleanFormData.attorneys.forEach((attorney) => {
          if (attorney.transcriptOrders && attorney.transcriptOrders.length > 0) {
            attorney.transcriptOrders.forEach((order) => {
              if (!order.orderSubmissionFiles?.length) {
                const newAttorneyFiles = window.__NEW_ATTORNEY_FILES__;
                if (newAttorneyFiles && newAttorneyFiles.size > 0) {
                  const key = `${attorney.id}:${order.deponentId}`;
                  if (newAttorneyFiles.has(key)) {
                    const files = newAttorneyFiles.get(key) ?? [];
                    order.orderSubmissionFiles = [...files];
                  }
                }
              }
            });
          }
        });
      }
      
      // Step 4: Submit the form with file references
      const response = await fetch(`${API_URL}/api/jobs/submit`, {
        method: 'POST',
        credentials: 'include', // Send the reporter session cookie, if any, for log traceability
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: formData.token,
          formData: cleanFormData
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to submit form: ${errorText}`);
      }
      
      const result = await response.json();
      devLog('Server response:', result);
      
      setSubmitSuccess(true);
      setIsFormDirty(false);
      
      // Return the confirmation ID from the server response
      return { 
        success: true,
        confirmationId: result.confirmationId
      };
    } catch (error) {
      console.error('Error submitting form:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };
  
  // Validate form data for a specific step
  // Helper function to check if an attorney needs a mailing address
  const attorneyNeedsMailingAddress = (attorney: Attorney): boolean => {
    return attorney.transcriptOrders.some(order => {
      return (
        (order.orderType === 'Original' || order.orderType === 'Copy' || order.orderType === 'FREE Copy') &&
        (order.transcriptFormat === 'Physical Only' || order.transcriptFormat === 'Email & Physical')
      );
    });
  };

  const validateStep = (stepId: string): boolean => {
    const errors: {[key: string]: string} = {};
    
    switch (stepId) {
      case 'basic-info':
        if (!formData.jobNumber) errors.jobNumber = 'Job number is required';
        if (!formData.jobDate) errors.jobDate = 'Job date is required';
        if (!formData.reporterName) errors.reporterName = 'Reporter name is required';
        if (!formData.jobType) errors.jobType = 'Job type is required';
        break;
        
      case 'deponent-info':
        if (formData.deponents.length === 0) {
          errors.deponents = 'At least one deponent is required';
        } else {
          formData.deponents.forEach((deponent, index) => {
            if (!deponent.name) {
              errors[`deponent-${index}-name`] = 'Deponent name is required';
            }
            
            // Check Read & Sign requirement
            if (deponent.readAndSign === undefined) {
              errors[`deponent-${index}-readAndSign`] = 'Please select Yes or No for Read & Sign';
            }
            
            // Check transcript-related requirements
            if (deponent.transcriptType === 'standard' || !deponent.transcriptType) {
              if (!deponent.transcriptFiles || deponent.transcriptFiles.length === 0) {
                errors[`deponent-${index}-transcriptFiles`] = 'Transcript files are required';
              }
            }
            
            if (deponent.transcriptType === 'noTranscribe') {
              if (!deponent.audioFiles || deponent.audioFiles.length === 0) {
                errors[`deponent-${index}-audioFiles`] = 'Audio files or rough notes are required for no-transcribe';
              }
            }
            
            // No validation needed for RealLegal transcripts since they're submitted externally
            
            // Check if exhibit files are required
            if (deponent.exhibitStatus === 'Electronic exhibit(s)') {
              if (!deponent.exhibitFiles || deponent.exhibitFiles.length === 0) {
                errors[`deponent-${index}-exhibitFiles`] = 'Exhibit files are required for electronic exhibits';
              }
            }
          });
        }
        break;
        
      case 'attorney-info':
        // Check if any attorneys need mailing address but don't have one
        formData.attorneys.forEach((attorney, index) => {
          if (attorneyNeedsMailingAddress(attorney) && !attorney.mailingAddress) {
            errors[`attorney-${index}-mailingAddress`] = 'Mailing address is required for physical delivery';
          }
          
          // Check for expedited orders without due date
          if (attorney.transcriptOrders && Array.isArray(attorney.transcriptOrders)) {
            attorney.transcriptOrders.forEach((order, orderIndex) => {
              if (order.deliveryType === 'Expedited' && !order.dueDate) {
                errors[`attorney-${index}-order-${orderIndex}-dueDate`] = 'Due date is required for expedited orders';
              }
            });
          }
        });
        break;
        
        
      case 'pay-info':
        if (!formData.attendanceType) errors.attendanceType = 'Attendance type is required';
        break;
        
      default:
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Update form data when initialData changes (e.g., after PIN verification)
  useEffect(() => {
    if (initialData) {
      devLog("FormContext: Received new initialData", initialData);
      
      // Create a proper merged data object with all required fields
      const newMergedData = { ...formData };
      
      // Update basic fields
      if (initialData.jobNumber) newMergedData.jobNumber = initialData.jobNumber;
      if (initialData.jobDate) newMergedData.jobDate = initialData.jobDate;
      if (initialData.jobStartTime) newMergedData.jobStartTime = initialData.jobStartTime;
      if (initialData.caseName) newMergedData.caseName = initialData.caseName;
      if (initialData.jobState) newMergedData.jobState = initialData.jobState;
      if (initialData.jobType) newMergedData.jobType = initialData.jobType;
      if (initialData.reporterName) newMergedData.reporterName = initialData.reporterName;
      if (initialData.reporterEmail) newMergedData.reporterEmail = initialData.reporterEmail;
      
      // Update deponents if they exist
      if (initialData.deponents && Array.isArray(initialData.deponents) && initialData.deponents.length > 0) {
        newMergedData.deponents = initialData.deponents;
      }
      
      // Update attorneys if they exist
      if (initialData.attorneys && Array.isArray(initialData.attorneys) && initialData.attorneys.length > 0) {
        newMergedData.attorneys = initialData.attorneys;
      }
      
      devLog("FormContext: Setting new form data", newMergedData);
      setFormData(newMergedData);
    }
  // ESLINT NOTE: Intentionally omitting formData from deps — this effect merges initialData into formData,
  // so adding formData would create an infinite loop (effect runs → updates formData → triggers effect).
  // This should only fire when new data arrives from the server (initialData changes).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Load draft when job is initialized (only runs once per job number)
  useEffect(() => {
    const loadExistingDraft = async () => {
      // Only try to load draft if we have a job number and haven't loaded a draft for this job yet
      if (!formData.jobNumber || draftLoadedRef.current === formData.jobNumber) {
        return;
      }

      // Mark this job as having a draft load attempt
      draftLoadedRef.current = formData.jobNumber;
      devLog('Checking for existing draft for job:', formData.jobNumber);

      // Set loading state
      setDraftStatus(prev => ({ ...prev, isLoading: true }));

      try {
        const result = await apiLoadDraft(formData.jobNumber);

        devLog('API returned result:', { success: result.success, hasDraft: !!result.draft });

        if (result.success && result.draft) {
          devLog('Found existing draft, loading:', result.draft);

          // Merge draft data with current form data
          // Draft data takes precedence over initial data for user-entered fields
          const mergedData = {
            ...formData, // Start with initial job data
            ...result.draft.formData, // Override with draft data
            jobNumber: formData.jobNumber, // Always keep the job number from initial data
            token: formData.token || result.draft.token // Preserve token
          } as JobFormData;

          // Upgrade legacy drafts (schemaVersion < 2.0) in memory. The upgrade
          // only persists when the user next saves.
          const draftSchemaVersion = (result.draft.formData?.schemaVersion as string | undefined) ?? '1.0';
          if (draftSchemaVersion !== CURRENT_SCHEMA_VERSION) {
            upgradeFormDataToV2(mergedData as JobFormData & Record<string, unknown>);
            devLog(`Upgraded draft worksheet from schemaVersion ${draftSchemaVersion} to ${CURRENT_SCHEMA_VERSION}`);
          }

          setFormData(mergedData);
          setDraftStatus({
            isSaving: false,
            isLoading: false,
            lastSaved: new Date(result.draft.lastSavedAt),
            error: undefined,
            savedStep: result.draft.currentStep
          });

          devLog('Draft loaded successfully, resuming at step:', result.draft.currentStep);
        } else {
          devLog('No existing draft found', { resultSuccess: result.success, hasDraft: !!result.draft, error: result.error });
          setDraftStatus(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Error loading draft:', error);
        setDraftStatus(prev => ({ ...prev, isLoading: false }));
        // Don't show error to user - just continue with initial data
      }
    };

    loadExistingDraft();
  // ESLINT NOTE: Intentionally omitting formData — adding it would re-trigger the draft load on every form
  // field change, defeating the draftLoadedRef guard. This should only run when the job changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.jobNumber]); // Only run when job number changes

  // Save form data periodically to prevent data loss
  useEffect(() => {
    if (isFormDirty) {
      const saveTimer = setTimeout(() => {
        // Save to localStorage or to backend
        localStorage.setItem('formDataDraft', JSON.stringify(formData));
      }, 5000); // Save after 5 seconds of inactivity
      
      return () => clearTimeout(saveTimer);
    }
  }, [formData, isFormDirty]);
  
  // Extract file references from formData for draft saving
  const extractFileReferences = (): ExtractedFileRefs => {
    const fileRefs: ExtractedFileRefs = { deponents: {}, attorneys: {} };

    const toFileRef = (file: FormFile): FileRef => ({
      filename: file.serverData?.filename || file.name,
      originalName: file.serverData?.originalName || file.name,
      storagePath: file.serverData?.storagePath,
      size: file.size,
      mimetype: file.type
    });

    formData.deponents.forEach(deponent => {
      const deponentRefs: DeponentFileRefs = {};

      if (deponent.transcriptFiles && deponent.transcriptFiles.length > 0) {
        deponentRefs.transcriptFiles = deponent.transcriptFiles.map(toFileRef);
      }
      if (deponent.exhibitFiles && deponent.exhibitFiles.length > 0) {
        deponentRefs.exhibitFiles = deponent.exhibitFiles.map(toFileRef);
      }
      if (deponent.audioFiles && deponent.audioFiles.length > 0) {
        deponentRefs.audioFiles = deponent.audioFiles.map(toFileRef);
      }

      if (Object.keys(deponentRefs).length > 0) {
        fileRefs.deponents[deponent.id] = deponentRefs;
      }
    });

    formData.attorneys.forEach(attorney => {
      const attorneyRefs: AttorneyFileRefs = {};

      attorney.transcriptOrders.forEach((order, orderIndex) => {
        if (order.orderSubmissionFiles && order.orderSubmissionFiles.length > 0) {
          if (!attorneyRefs.orders) {
            attorneyRefs.orders = {};
          }
          attorneyRefs.orders[orderIndex] = {
            deponentId: order.deponentId,
            orderSubmissionFiles: order.orderSubmissionFiles.map(toFileRef)
          };
        }
      });

      if (Object.keys(attorneyRefs).length > 0) {
        fileRefs.attorneys[attorney.id] = attorneyRefs;
      }
    });

    if (Array.isArray(formData.invoiceFiles) && formData.invoiceFiles.length > 0) {
      fileRefs.invoiceFiles = formData.invoiceFiles.map(toFileRef);
    }

    return fileRefs;
  };

  // Save draft to server
  const saveDraft = async (currentStep: number): Promise<{ success: boolean; error?: string }> => {
    // Don't save if no job number
    if (!formData.jobNumber) {
      console.warn('Cannot save draft: missing job number');
      return { success: false, error: 'Missing required information' };
    }

    setDraftStatus(prev => ({ ...prev, isSaving: true }));

    try {
      // Extract file references
      const fileReferences = extractFileReferences();

      // Cast: our FileRef types have optional fields while FileMetadata requires them;
      // in practice, extractFileReferences only runs on already-uploaded server files.
      const result = await apiSaveDraft(
        formData.jobNumber,
        formData,
        currentStep,
        formData.token,
        fileReferences as FileReferences
      );

      if (result.success) {
        setDraftStatus({
          isSaving: false,
          isLoading: false,
          lastSaved: new Date(),
          error: undefined,
          savedStep: currentStep
        });
        setIsFormDirty(false);
        return { success: true };
      } else {
        setDraftStatus(prev => ({
          ...prev,
          isSaving: false,
          error: result.error
        }));
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setDraftStatus(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage
      }));
      return { success: false, error: errorMessage };
    }
  };

  // Delete draft from server
  const deleteDraft = async (): Promise<{ success: boolean; error?: string }> => {
    if (!formData.jobNumber) {
      return { success: false, error: 'Missing job number' };
    }

    try {
      // Call API to delete draft (reporter ID extracted from session on server)
      const result = await apiDeleteDraft(formData.jobNumber);

      if (result.success) {
        setDraftStatus({ isSaving: false, isLoading: false, lastSaved: undefined, error: undefined });
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  const contextValue: FormContextType = {
    formData,
    validationErrors,
    isFormDirty,
    submitSuccess,
    draftStatus,
    updateFormData,
    updateNestedFormData,
    updateMultipleNestedFields,
    addItemToArray,
    removeItemFromArray,
    handleFileUpload,
    removeFile,
    resetForm,
    submitForm,
    validateStep,
    saveDraft,
    deleteDraft
  };
  
  return (
    <FormContext.Provider value={contextValue}>
      {children}
    </FormContext.Provider>
  );
};

// Custom hook to use the form context
export const useFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};